/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CalendarMonthRounded from '@src/assets/icons/CalendarMonthRounded.svg';
import CalendarActive from '@src/assets/icons/CalendarActive.svg';
import FilterActive from '@src/assets/icons/FilterActive.svg';
import FilterAltFilled from '@src/assets/icons/FilterAltFilled.svg';
import CircleBlue from '@src/assets/icons/circleblue.svg';
import moment from 'moment';
import {
  IconButton,
  TextField,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  InputAdornment,
  Box,
  Popper,
  Fade,
  Paper,
  // Button
} from '@mui/material';
import {
  RestartAltOutlined,
  Close,
  DeleteOutlineOutlined,
  DownloadOutlined,
  // KeyboardArrowDown,
  // KeyboardArrowUp,
  Search,
  FilterList,
  CalendarToday,
} from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { LocalizationProvider, StaticDatePicker } from '@mui/lab';
import { vi, enGB } from 'date-fns/locale';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { REQUEST_STATUS } from '@src/constants/voice';
import { LANGUAGE, PAGINATION_LIMIT } from '@src/constants';
import { FETCH_REQUESTS_INTERVAL } from '@src/constants/websocket';
// import debounce from '@src/utils/debounce';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
import Tooltip from '@src/components/Tooltip';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import NotificationDialog from '@src/components/NotificationDialog';
import actions from '@src/redux/actions';
import { debounce } from 'lodash';
import {
  FIELDS_FILTER,
  MAX_AUDIO_DOWNLOAD,
  MAX_REQUEST_DELETE,
} from '@src/constants/request';
import { downloadAudioLinks } from '@src/apis/requests';
import { trackAudioDownloaded } from '@src/services/moengage';
import { MOE_MODULE, MOE_SURFACE } from '@src/constants/moengage';
import CustomDatePickerRange from '@src/components/CustomDatePickerRange';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import i18n from '@src/languages';
import RequestTable from './RequestTable';

import { StyledRequests, StyledActionWarning } from './index.style';
import RequestsActionHeader from './RequestsActionHeader';

const initialFilter = {
  status: '',
  createdAt: [null, null],
};

const Requests = ({
  isNewHomepage,
  requestListHeight,
  flickerBackground,
  isExpand,
  requestLoading,
  onChangeIsExpand,
  onChangeRequestLoading,
  onEnableEditor,
  audioRef,
  onClickAddRequest,
}) => {
  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);

  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_LIMIT);
  const [filter, setFilter] = useState(initialFilter);
  const [sort, setSort] = useState('createdAt_desc');
  const [loading, setLoading] = useState(false);
  const [numSelected, setNumSelected] = useState(0);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [validateDeleteRequest, setValidateDeleteRequest] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [allowDownload, setAllowDownload] = useState(false);
  const [anchorStatusRequest, setAnchorStatusRequest] = useState(null);
  const [openPopperStatusRequest, setOpenPopperStatusRequest] = useState(false);
  const [selectedFields, setSelectedFields] = useState(
    Object.values(FIELDS_FILTER),
  );
  const [anchorFieldFilter, setAnchorFieldFilter] = useState(null);
  const [activeResetButton, setActiveResetButton] = useState(false);
  const [anchorRequestStatusFilter, setAnchorRequestStatusFilter] =
    useState(null);
  const [anchorDateFilter, setAnchorDateFilter] = useState(null);
  const refClickOutSide = useClickOutside(() =>
    setOpenPopperStatusRequest(false),
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const requestsRef = useRef(requests);
  const [page, setPage] = useState(0);
  const filterRef = useRef(filter);
  const sortRef = useRef(sort);
  const { languages } = useSelector((state) => state.language);
  const responseRequest = useSelector((state) => state.request);
  const { synthesisRequest } = useSelector((state) => state.synthesisRequest);
  const isMobileScreen = window.innerWidth < 480;
  const { language } = i18n;

  const updateCurrentSynthesisRequest = (listRequest) => {
    const currentRequest = listRequest.find(
      (obj) => obj.id === synthesisRequest.id,
    );

    if (currentRequest) {
      const { voice } = currentRequest;
      let voiceLanguage = languages.find(
        (lang) => lang?.value === voice?.languageCode,
      );

      voiceLanguage = { ...voiceLanguage, code: voiceLanguage?.value };
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig(
          {
            ...synthesisRequest,
            ...currentRequest,
            voice: { ...currentRequest.voice, language: voiceLanguage },
          },
          true,
        ),
      );
    }
  };

  const requestsDataTable = useMemo(() => requests, [requests]);

  const handleUpdateRequests = (newRequests) => {
    setRequests(newRequests);
    updateCurrentSynthesisRequest(newRequests);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPage(0);
    setOpenPopperStatusRequest(false);
  };

  const handleChangeSort = (newSort) => {
    setSort(newSort);
    setPage(1);
  };

  const handleResetFilter = () => {
    setFilter(initialFilter);
    setSearch('');
    setPage(0);
  };

  const handleChangeSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleSelectFields = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter((f) => f !== field));
    } else setSelectedFields([...selectedFields, field]);
  };

  const offsetRequest = useMemo(() => page * rowsPerPage, [rowsPerPage, page]);

  const handleChangeDatePickerRange = (value) =>
    handleChangeFilter('createdAt', value);
  const fetchRequests = async (hasLoading) => {
    if (hasLoading) setLoading(true);
    const { status, createdAt } = filterRef.current;

    const requestParams = {
      search: search.trim(),
      sort: sortRef.current,
      // ...(!isNewHomepage
      //   ? {
      //       limit: useNewPagination ? rowsPerPage : limit,
      //       offset: offsetRequest,
      //     }
      //   : {}),
      limit: rowsPerPage,
      offset: offsetRequest,
      startDate: createdAt[0]
        ? moment(createdAt[0]).startOf('day').toISOString()
        : undefined,
      endDate: createdAt[1]
        ? moment(createdAt[1]).endOf('day').toISOString()
        : undefined,
      status,
      fields:
        'id,title,characters,credits,createdAt,progress,status,voice,audioType,audioLink,retentionPeriod,processingAt,endedAt',
    };

    const data = await apis.requests.getRequestsV2(requestParams);

    setLoading(false);
    onChangeRequestLoading(false);
    if (data && data.status) {
      const newRequests = data.result.requests.map((request) => {
        const req = requests.find((r) => r.id === request.id);
        if (req) {
          return {
            ...request,
            progress:
              req?.progress && request.progress < req?.progress
                ? req.progress
                : request.progress,
          };
        }
        return request;
      });

      setRequests(newRequests);
      setTotalRequests(data.result.metadata.total);
      updateCurrentSynthesisRequest(newRequests);
    }
  };

  const fetchProgressRequest = async (requestId) => {
    const data = await apis.requests.getProgressRequest(requestId);
    if (data && data.status) {
      const { progress, status, audioLink, endedAt, processingAt } =
        data.result;
      const newRequests = requestsRef.current.map((request) => {
        if (request.id === requestId) {
          return {
            ...request,
            progress,
            status,
            audioLink,
            processingAt: processingAt || request.processingAt,
            endedAt: endedAt || request.endedAt,
          };
        }
        return request;
      });
      handleUpdateRequests(newRequests);
      // Polling is the only channel left when a websocket frame is lost, and
      // the store's copy is what the editor and every other view read.
      dispatch(
        actions.request.addSynthesisResponse({
          requestId,
          progress,
          status,
          audioLink,
          processingAt,
          endedAt,
        }),
      );
    }
  };

  const handleCloseDeleteWarningDialog = () => {
    setDeleteWarning(false);
  };

  const handleCloseValidateDeleteRequestDialog = () => {
    setValidateDeleteRequest(false);
  };

  const handleCloseFieldsFilter = () => setAnchorFieldFilter(null);

  const handleDeleteSelectRequest = () => {
    const numSelectedRequest = selectedRequests.length;
    if (numSelectedRequest > 0 && numSelectedRequest <= MAX_REQUEST_DELETE) {
      setDeleteWarning(true);
    } else {
      setValidateDeleteRequest(true);
    }
  };

  const handleDownloadAudios = async () => {
    if (selectedRequests.length > MAX_AUDIO_DOWNLOAD) {
      setValidateDeleteRequest(true);
      return;
    }
    const listPresignedUrl = [];
    const listAudioId = selectedRequests.map((req) => ({
      id: req.id,
      title: req.title,
      userId: req.userId,
    }));

    const promises = listAudioId.map(async ({ id, title }) => {
      try {
        const res = await apis.requests.getPresignedAudioUrl(id);
        if (res?.status) {
          listPresignedUrl.push(res.result?.audio);
        } else {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: 'downloadError',
              value: title,
            }),
          );
        }
      } catch (error) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'downloadError',
            value: title,
          }),
        );
      }
    });

    await Promise.all(promises);
    trackAudioDownloaded({
      module: MOE_MODULE.TTS,
      surface: MOE_SURFACE.BULK,
      count: listPresignedUrl.length,
    });
    downloadAudioLinks(listPresignedUrl);
  };

  const handleSelectRequest = (selectReqs) => {
    setSelectedRequests(selectReqs);
    const allowDownloadAudios = selectReqs.every((req) => req.audioLink);
    setAllowDownload(allowDownloadAudios);
  };

  const handleDeleteRequestConfirm = async () => {
    try {
      const requestIds = selectedRequests.map((req) => req.id);
      const data = await apis.requests.deleteRequest({ requestIds });
      if (!data?.status) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'deleteRequestFailure',
          }),
        );
        setDeleteWarning(false);
        return;
      }
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'deleteRequestSuccess',
        }),
      );
      setDeleteWarning(false);
      setSelectedRequests([]);
      setPage(0);
      fetchRequests(false);
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: error?.response?.data?.error_message,
        }),
      );
      setDeleteWarning(false);
    }
  };

  const updateInprogressRequest = (requestIds) => {
    Promise.all(
      requestIds.map(async (requestId) => {
        await fetchProgressRequest(requestId);
        return null;
      }),
    );
  };

  const updateRequest = (requestId, updateInfo) => {
    const newRequests = requestsRef.current.map((request) => {
      if (request.id === requestId) {
        return {
          ...request,
          ...updateInfo,
        };
      }
      return request;
    });
    handleUpdateRequests(newRequests);
  };

  const renderConfirmDeleteRequestACtion = (handleClose, handleConfirm) => (
    <StyledActionWarning>
      <Button
        variant="outlined"
        // data-id="cancel-delete-req"
        onClick={handleClose}
      >
        {t('noValue')}
      </Button>
      <Button
        variant="contained"
        onClick={handleConfirm}
        // data-id="confirm-delete-req"
      >
        {t('yesValue')}
      </Button>
    </StyledActionWarning>
  );

  const renderCloseButton = (handleClose) => (
    <Button variant="contained" onClick={handleClose}>
      {t('understood')}
    </Button>
  );

  const handleSelectAll = (event, checked = false) => {
    if (event.target.checked || checked) {
      const newSelected = requestsDataTable.map((rowData) => {
        const {
          id,
          title,
          audioLink,
          status,
          retentionPeriod,
          createdAt,
          latestRequestId,
        } = rowData;
        return {
          id,
          title,
          audioLink,
          status,
          retentionPeriod,
          createdAt,
          latestRequestId,
        };
      });

      setNumSelected(newSelected.length);
      if (handleSelectRequest)
        handleSelectRequest([
          ...new Map(
            [...selectedRequests, ...newSelected].map((item) => [
              item.id,
              item,
            ]),
          ).values(),
        ]);

      return;
    }

    if (handleSelectRequest) {
      const newSelected = selectedRequests.filter(
        (rowSelected) =>
          !requestsDataTable
            .map((rowData) => rowData.id)
            .includes(rowSelected.id),
      );
      handleSelectRequest(newSelected);
      setNumSelected(0);
    }
  };

  const tooltipDateFilter = useMemo(() => {
    const tooltipText = filter?.createdAt
      ?.filter(Boolean)
      ?.map((data) => moment(data).format('DD/MM/YYYY'))
      ?.join(' - ');
    return tooltipText;
  }, [filter?.createdAt]);

  const renderDesktopActionTable = () => {
    if (!isExpand) return <></>;

    return (
      <div className="action-table">
        <div className="request-filter">
          <div
            data-search-keyword={filter?.title}
            className="filter-item"
            role="button"
            tabIndex="0"
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              size="small"
              className="text-field-request filter-title"
              variant="outlined"
              hiddenLabel={false}
              value={filter?.title}
              placeholder={t('newFindTitle')}
              onChange={handleChangeSearch}
              data-id="search-req"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <div
              className="filter-item"
              role="button"
              tabIndex="0"
              onClick={(e) => e.stopPropagation()}
            >
              <div data-id="filter-req-by-date" className="date-field">
                <CustomDatePickerRange
                  value={filter.createdAt}
                  onChange={handleChangeDatePickerRange}
                >
                  <Tooltip arrow title={tooltipDateFilter} placement="top">
                    {filter.createdAt[0] ? (
                      <img
                        style={{ width: '24px' }}
                        src={CalendarActive}
                        alt="CalendarMonthRounded"
                      />
                    ) : (
                      <img
                        src={CalendarMonthRounded}
                        alt="CalendarMonthRounded"
                      />
                    )}
                  </Tooltip>
                  {!!filter.createdAt[0] && (
                    <img
                      className="circle-btn"
                      src={CircleBlue}
                      alt="FilterAltFilled"
                    />
                  )}
                </CustomDatePickerRange>
              </div>
            </div>
            <Tooltip
              arrow
              title={filter.status ? t(filter.status) : ''}
              placement="top"
            >
              <IconButton
                data-id="open-req-filter-by-status"
                className="filter-btn"
                ref={refClickOutSide}
                onClick={(e) => {
                  setAnchorStatusRequest(e.currentTarget);
                  setOpenPopperStatusRequest((prev) => !prev);
                }}
              >
                {filter.status ? (
                  <img
                    style={{ width: '24px' }}
                    src={FilterActive}
                    alt="FilterAltFilled"
                  />
                ) : (
                  <img src={FilterAltFilled} alt="FilterAltFilled" />
                )}

                {filter.status && (
                  <img
                    className="circle-btn"
                    src={CircleBlue}
                    alt="FilterAltFilled"
                  />
                )}
              </IconButton>
            </Tooltip>

            <Popper
              transition
              style={{ zIndex: 1000, width: '180px', color: '#1E164E' }}
              anchorEl={anchorStatusRequest}
              open={openPopperStatusRequest}
              placement="bottom-start"
            >
              {({ TransitionProps }) => (
                <Fade
                  style={{ padding: '5px 0px' }}
                  {...TransitionProps}
                  timeout={350}
                >
                  <Paper>
                    {Object.keys(REQUEST_STATUS).map((item) => (
                      <MenuItem
                        style={{
                          fontSize: '14px',
                          padding: '8px',
                          color: '#1E164E',
                          background:
                            filter?.status === item && COLOR.activeColor,
                        }}
                        onClick={() => {
                          handleChangeFilter('status', item);
                        }}
                        key={item}
                        value={item}
                        data-id="filter-by-req-status"
                        data-status={item}
                      >
                        {t(item)}
                      </MenuItem>
                    ))}
                  </Paper>
                </Fade>
              )}
            </Popper>
          </div>

          <Menu
            anchorEl={anchorFieldFilter}
            open={Boolean(anchorFieldFilter)}
            onClose={handleCloseFieldsFilter}
            className="fields-filter"
          >
            {Object.keys(FIELDS_FILTER).map((key) => (
              <MenuItem
                key={key}
                value={FIELDS_FILTER[key]}
                onClick={() => handleSelectFields(FIELDS_FILTER[key])}
              >
                <Checkbox
                  checked={selectedFields.includes(FIELDS_FILTER[key])}
                  onClick={handleSelectFields}
                  style={{ color: COLOR.darkBlue }}
                />
                <div>{t(key)}</div>
              </MenuItem>
            ))}
          </Menu>
          <Tooltip arrow title={t('reset')} placement="bottom">
            <div>
              <IconButton
                data-id="reset-req-filter"
                className={`filter-btn ${
                  !activeResetButton ? 'disable-color' : 'actived-filter'
                }`}
                onClick={handleResetFilter}
                disabled={!activeResetButton}
              >
                <RestartAltOutlined color="#1E164EA3" />
              </IconButton>
            </div>
          </Tooltip>
        </div>
      </div>
    );
  };
  const renderMobileActionTable = () => (
    <div className="action-table">
      <div className="request-filter">
        <Box className="filter-item" onClick={(e) => e.stopPropagation()}>
          <TextField
            size="small"
            className="text-field-request filter-title"
            variant="outlined"
            value={filter.title}
            label={t('findTitle')}
            onChange={handleChangeSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box
            className="filter-status"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorRequestStatusFilter(e.currentTarget);
            }}
          >
            <FilterList />
          </Box>
          <Box
            className="filter-status"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorDateFilter(e.currentTarget);
            }}
          >
            <CalendarToday />
          </Box>
          <Box>
            <IconButton
              className={`refresh-icon ${
                !activeResetButton && 'disable-color'
              }`}
              onClick={handleResetFilter}
              disabled={!activeResetButton}
            >
              <RestartAltOutlined />
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorRequestStatusFilter}
            open={Boolean(anchorRequestStatusFilter)}
            onClose={() => setAnchorRequestStatusFilter(null)}
            className="fields-filter"
          >
            {Object.keys(REQUEST_STATUS).map((item) => (
              <MenuItem
                key={item}
                value={item}
                onClick={() => {
                  handleChangeFilter('status', item);
                  setAnchorRequestStatusFilter(null);
                }}
              >
                {t(item)}
              </MenuItem>
            ))}
          </Menu>
          <Menu
            anchorEl={anchorDateFilter}
            open={Boolean(anchorDateFilter)}
            onClose={() => setAnchorDateFilter(null)}
          >
            <div className="date-field">
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                localeText={{ start: t('start'), end: t('end') }}
                locale={language === LANGUAGE.VN ? vi : enGB}
              >
                <StaticDatePicker
                  open
                  onClose={() => setAnchorDateFilter(null)}
                  value={filter.createdAt}
                  onChange={(newValue) =>
                    handleChangeDatePickerRange([newValue])
                  }
                  pickerProps={{ readOnly: true }}
                  displayStaticWrapperAs="desktop"
                />
              </LocalizationProvider>
            </div>
          </Menu>
        </Box>
      </div>
    </div>
  );

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    sortRef.current = sort;
  }, [sort]);

  useEffect(() => {
    fetchRequests(true);
  }, [page, rowsPerPage, offsetRequest]);

  useEffect(() => {
    fetchRequests(true);
  }, [filter, sort]);

  useEffect(() => {
    debounce(fetchRequests, 500)(search);
  }, [search]);

  useEffect(() => {
    if (requestLoading) {
      fetchRequests(false);
    }
  }, [requestLoading]);

  useEffect(() => {
    const newRequests = requests.map((request) => {
      const isInProgressRequest =
        request.status === REQUEST_STATUS.IN_PROGRESS &&
        responseRequest[request.id];
      if (isInProgressRequest || !request.processingAt) {
        request.status = responseRequest[request.id]?.status || request.status;
        request.audioLink =
          responseRequest[request.id]?.audioLink || request.audioLink;
        request.progress =
          responseRequest[request.id]?.progress || request.progress;
        request.processingAt =
          responseRequest[request.id]?.processingAt || request.processingAt;
        request.endedAt =
          responseRequest[request.id]?.endedAt || request.endedAt;
      }
      return request;
    });
    handleUpdateRequests(newRequests);
  }, [responseRequest]);

  useEffect(() => {
    const fetchRequestsInterval = setInterval(async () => {
      const inProgressRequest = requestsRef.current
        .filter((request) => request.status === REQUEST_STATUS.IN_PROGRESS)
        .map((request) => request.id);
      if (inProgressRequest?.length) updateInprogressRequest(inProgressRequest);
    }, FETCH_REQUESTS_INTERVAL);

    return () => clearInterval(fetchRequestsInterval);
  }, [synthesisRequest?.id]);

  useEffect(() => {
    updateCurrentSynthesisRequest(requests);
  }, [synthesisRequest?.id]);

  useEffect(() => {
    const { status, createdAt } = filter;
    const { status: defaultStatus } = initialFilter;

    const isFilterStatus = status !== defaultStatus;
    const isFilterDate = createdAt[0] || createdAt[1];
    const isFilterSearch = search.trim() !== '';
    const pageCurrent = page !== 0;
    const isFilter =
      isFilterStatus || isFilterDate || isFilterSearch || pageCurrent;
    setActiveResetButton(isFilter);
  }, [filter, search, page]);
  const CLASS_TARGET_EXPAND = 'request-list-title';
  return (
    <StyledRequests
      onClick={(event) => {
        if (
          typeof event.target.className !== 'string' ||
          !!selectedRequests?.length
        )
          return;
        if (
          event.target.className === CLASS_TARGET_EXPAND ||
          event.target.className?.includes('action-header')
        ) {
          onChangeIsExpand(!isExpand);
        }
      }}
      isWrapperClosed={!isExpand}
      heightCurrent="auto"
    >
      <div className="request-wrapper" role="button" tabIndex="0">
        <div
          className="request-list-title"
          data-id={isExpand ? 'closed-req-list' : 'open-req-list'}
        >
          {!isNewHomepage ? (
            <>
              {!selectedRequests?.length && (
                <div
                  onClick={() => onChangeIsExpand(!isExpand)}
                  role="button"
                  tabIndex="0"
                >
                  <IconButton className="expand-icon" size="small">
                    {isExpand ? (
                      <KeyboardArrowDownRoundedIcon />
                    ) : (
                      <ChevronRightRoundedIcon />
                    )}
                  </IconButton>
                </div>
              )}
              {selectedRequests?.length > 0 && (
                <div
                  onClick={() => setSelectedRequests([])}
                  role="button"
                  tabIndex="0"
                >
                  <IconButton className="expand-icon" size="small">
                    <ClearRoundedIcon />
                  </IconButton>
                </div>
              )}
            </>
          ) : (
            <></>
          )}

          {!selectedRequests?.length ? (
            <div
              onClick={() => onChangeIsExpand(!isExpand)}
              role="button"
              tabIndex="0"
              className="title-history-requests-wrapper"
            >
              <span className="title-history-requests">
                {t('historyRequest')}
              </span>

              {isNewHomepage && (
                <button
                  className="button-add-product"
                  type="button"
                  onClick={onClickAddRequest}
                >
                  <AddRoundedIcon />
                </button>
              )}
            </div>
          ) : (
            <RequestsActionHeader
              isShowBtnSelectAll={numSelected < rowsPerPage && !loading}
              isShowBtnDeselectAll={numSelected === rowsPerPage && !loading}
              handleSelectAll={handleSelectAll}
              selectedRequests={selectedRequests}
              onDeleteSelectedRequests={handleDeleteRequestConfirm}
              data-id="close-req-list"
            />
          )}
        </div>
        {isMobileScreen
          ? renderMobileActionTable()
          : !selectedRequests?.length && renderDesktopActionTable()}
      </div>
      <div id={TTS_GUIDE.REQUESTS_TABLE}>
        <RequestTable
          isExpand={isExpand}
          requests={requestsDataTable}
          selectedFields={selectedFields}
          requestListHeight={requestListHeight}
          onChangeRowPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          page={page}
          total={totalRequests}
          sort={sort}
          flickerBackground={[
            flickerBackground,
            !search,
            !filter.status,
            !filter.createdAt[0],
            !requestLoading,
          ]?.every(Boolean)}
          loading={loading}
          setNumSelected={setNumSelected}
          selectedRequests={selectedRequests}
          onUpdateRequest={updateRequest}
          onChangeSort={handleChangeSort}
          onChangePage={handleChangePage}
          onSelectRequest={handleSelectRequest}
          onDeleteSelectedRequests={handleDeleteRequestConfirm}
          onEnableEditor={onEnableEditor}
          audioRef={audioRef}
        />
      </div>
      {selectedRequests?.length ? (
        <div className="select-request-popper" style={{ display: 'none' }}>
          <div className="">
            {t('selectRequests', { numRequests: selectedRequests.length })}
          </div>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteOutlineOutlined />}
            onClick={handleDeleteSelectRequest}
          >
            {t('delete')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={!allowDownload}
            startIcon={<DownloadOutlined />}
            onClick={handleDownloadAudios}
          >
            {t('download')}
          </Button>
          <IconButton color="secondary" onClick={() => handleSelectRequest([])}>
            <Close />
          </IconButton>
        </div>
      ) : null}
      <NotificationDialog
        name="ssml"
        title={t('deleteRequestTitle')}
        description={t('deleteRequestTitleDescription')}
        variant="warning"
        open={deleteWarning}
        onClose={handleCloseDeleteWarningDialog}
        actionComponent={renderConfirmDeleteRequestACtion(
          handleCloseDeleteWarningDialog,
          handleDeleteRequestConfirm,
        )}
      />
      <NotificationDialog
        name="ssml"
        title={t('exceedRequests')}
        description={t('validateRequestDescription', {
          maxRequests: MAX_REQUEST_DELETE,
        })}
        variant="warning"
        open={validateDeleteRequest}
        onClose={handleCloseValidateDeleteRequestDialog}
        actionComponent={renderCloseButton(
          handleCloseValidateDeleteRequestDialog,
        )}
      />
    </StyledRequests>
  );
};

export default Requests;
